import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Bot, Coins, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

// Frontend label → backend role value mapping.
// Backend still stores `consumer | lender | business` under the hood
// to avoid a DB migration; the UI rebrands them for the Agent Credit
// Protocol era.
const ROLES = [
  {
    value: 'consumer',
    label: 'Agent Operator',
    description: 'Deploy agents that borrow',
    icon: Bot,
    accent: 'text-emerald-400',
    accentBg: 'bg-emerald-500/15',
  },
  {
    value: 'lender',
    label: 'Credit Provider',
    description: 'Underwrite agent credit',
    icon: Coins,
    accent: 'text-amber-400',
    accentBg: 'bg-amber-500/15',
  },
  {
    value: 'business',
    label: 'Enterprise',
    description: 'Multi-agent treasury',
    icon: Building2,
    accent: 'text-purple-400',
    accentBg: 'bg-purple-500/15',
  },
];

export default function Auth() {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [role, setRole] = useState(searchParams.get('role') || 'consumer');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });

  const { login, register, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const getRedirectPath = (userRole) => {
    if (userRole === 'admin') return '/admin';
    if (userRole === 'business') return '/business';
    if (userRole === 'lender') return '/lender';
    return '/consumer';
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(getRedirectPath(user.role));
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        toast({
          title: 'Welcome back',
          description: `Signed in as ${userData.name}`,
        });
        navigate(getRedirectPath(userData.role));
      } else {
        if (!formData.name.trim()) {
          toast({ title: 'Error', description: 'Please enter your name', variant: 'destructive' });
          setLoading(false);
          return;
        }
        const userData = await register(formData.email, formData.password, formData.name, role);
        toast({
          title: 'Account created',
          description: `Welcome to Kojiru, ${userData.name}`,
        });
        navigate(getRedirectPath(userData.role));
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Authentication failed',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <span className="text-xl font-bold">K</span>
            </div>
            <span className="text-xl font-bold">Kojiru</span>
          </div>

          <h1 className="text-3xl font-bold mb-2">
            {isLogin ? 'Welcome Back' : 'Join the Agent Credit Protocol'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {isLogin
              ? 'Sign in to access your credit lines, agents, and ACS score.'
              : 'Create an account to deploy agents, underwrite credit, or manage a fleet.'}
          </p>

          {/* Prominent Sign In / Sign Up Tabs */}
          <div
            className="grid grid-cols-2 gap-1 p-1 mb-8 bg-white/[0.04] border border-white/[0.06] rounded-xl"
            data-testid="auth-tabs"
          >
            <button
              type="button"
              data-testid="auth-tab-signin"
              onClick={() => setIsLogin(true)}
              className={`py-2.5 rounded-lg text-sm font-medium transition-all ${
                isLogin
                  ? 'bg-white/[0.08] text-white shadow-sm'
                  : 'text-muted-foreground hover:text-white'
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              data-testid="auth-tab-signup"
              onClick={() => setIsLogin(false)}
              className={`py-2.5 rounded-lg text-sm font-medium transition-all ${
                !isLogin
                  ? 'bg-white/[0.08] text-white shadow-sm'
                  : 'text-muted-foreground hover:text-white'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Role Selection (Sign Up only) */}
          {!isLogin && (
            <div className="mb-6">
              <Label className="mb-3 block">I want to join as:</Label>
              <div className="grid grid-cols-3 gap-3">
                {ROLES.map(({ value, label, description, icon: Icon, accent, accentBg }) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`role-${value}-btn`}
                    onClick={() => setRole(value)}
                    className={`p-4 rounded-xl border transition-all text-center ${
                      role === value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-lg ${accentBg} flex items-center justify-center mx-auto mb-2`}
                    >
                      <Icon className={`w-5 h-5 ${accent}`} />
                    </div>
                    <span className="block font-medium text-sm">{label}</span>
                    <span className="text-xs text-muted-foreground">{description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete={isLogin ? 'on' : 'off'}>
            {!isLogin && (
              <div>
                <Label htmlFor="name">Full Name</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="name"
                    data-testid="name-input"
                    type="text"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 h-12 bg-white/5 border-white/10"
                    autoComplete="name"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email">Email Address</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-12 bg-white/5 border-white/10"
                  required
                  autoComplete={isLogin ? 'email' : 'off'}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 h-12 bg-white/5 border-white/10"
                  required
                  minLength={6}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                />
              </div>
            </div>

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 text-lg gradient-primary hover:opacity-90"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </span>
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {isLogin ? (
              <>
                New to Kojiru?{' '}
                <button
                  data-testid="toggle-to-signup-btn"
                  onClick={() => setIsLogin(false)}
                  className="text-primary hover:underline"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button
                  data-testid="toggle-to-signin-btn"
                  onClick={() => setIsLogin(true)}
                  className="text-primary hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </motion.div>
      </div>

      {/* Right Side - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-secondary/20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/20 rounded-full blur-[120px]" />

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 text-center p-12"
        >
          <div className="w-24 h-24 rounded-2xl gradient-primary flex items-center justify-center mx-auto mb-8 glow">
            <Bot className="w-12 h-12" />
          </div>
          <h2 className="text-3xl font-bold mb-4">
            {isLogin ? 'Access Your Agent Fleet' : 'Credit for the Machine Economy'}
          </h2>
          <p className="text-lg text-muted-foreground max-w-sm mx-auto mb-10">
            {isLogin
              ? 'Monitor credit lines, ACS scores, and task settlement in real time.'
              : 'The first bilateral credit protocol built for autonomous AI agents. Live on Base.'}
          </p>

          {/* USPs */}
          <div className="space-y-4 max-w-sm mx-auto" data-testid="auth-usps">
            <div className="p-4 rounded-xl bg-white/[0.04] border border-white/[0.06] backdrop-blur-sm text-left">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-emerald-400" />
                </div>
                <p className="text-sm font-medium text-gray-200" data-testid="usp-agents">
                  Give your agents credit lines in one line of code.
                </p>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.04] border border-white/[0.06] backdrop-blur-sm text-left">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                  <Coins className="w-4 h-4 text-amber-400" />
                </div>
                <p className="text-sm font-medium text-gray-200" data-testid="usp-lenders">
                  Underwrite agents with transparent on-chain ACS scores.
                </p>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.04] border border-white/[0.06] backdrop-blur-sm text-left">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-purple-400" />
                </div>
                <p className="text-sm font-medium text-gray-200" data-testid="usp-enterprise">
                  Manage multi-agent treasuries with per-task escrow.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
